import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, useMediaQuery, FormControlLabel, Button } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import BulletList from './bullet-list';
import NumberList from './number-list';
import Model3DViewer from './model3DViewer';
import Callout from './callout';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import HandoutImageManager from './handout-image-manager';
import HandoutImageLoader from './handout-image-loader';
import IconStrip from './icon-strip';
import Copyright from './copyright';
import { GlobalStyles } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import AntSwitch from '../../UIControls/AntSwitch';
import HandoutVisibilitySwitch, { handoutVisibilityToggleSx } from '../../UIControls/HandoutVisibilitySwitch';
import CTAUpgradeDialog from '../../UIControls/CTAUpgradeDialog';
import ctaUpgradeProfessionalPaywall from '../../../assets/banners/ctaUpgradeProfessionalPaywall.png';
import Whiteboard from './whiteboard/Whiteboard';
import DisclaimerNotice from './disclaimer-notice';
import { useUnsavedChanges } from '../UnsavedChangesContext';
import EditableBlock from './customise-mode/EditableBlock';
import EditableList from './customise-mode/EditableList';
import { useCustomiseMode } from './customise-mode/CustomiseModeContext';
import WhiteboardSelectionModal from './customise-mode/WhiteboardSelectionModal';
import UserApi from '../../../rest-api/user-api';
import FavouriteButton from './FavouriteButton';
import { isSavedHandoutFavourite, toggleSavedHandoutFavourite } from '../../../utils/saved-handout-favourites';
import { normalizeAccountType } from '../../../utils/subscription-plan-utils';
import HandoutContentProtection from './HandoutContentProtection';

function DisplayHandout({
    handoutData,
    showLabels,
    setShowLabels,
    show3DModel,
    showMainIllustration,
    showTutorialVideo,
    showWhiteboard,
    activeTool,
    color,
    thickness,
    canvasRef,
    onUnsavedChanges,
    setToolbarExpanded,
    showDisclaimer,
    doctorName,
    clinicName,
    suppressUnsavedChangesRef,
    onColorUsed,
    onCanvasModified, // Phase 1: Track canvas changes for combined mode
    hasActiveWhiteboardDrawings = false,
    activeCustomisedHandoutContext = null,
    disableCustomiseMode = false,
    onToggle3DModel,
    onToggleDiagram,
    onToggleTutorialVideo,
    onUploadLogoClick,
}) {
    const { authStore, imageStore, userDataStore, savedHandoutStore } = useContext(StoreContext);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const locale = 'en-UK';
    const {
        isCustomiseMode,
        getCurrentContentData,
        isLoadedCustomisedHandout,
        setWhiteboardIllustration,
        selectedWhiteboard,
    } = useCustomiseMode();
    const effectiveIsCustomiseMode = !disableCustomiseMode && isCustomiseMode;
    const effectiveShowWhiteboard = !disableCustomiseMode && showWhiteboard;
    const effectiveSelectedWhiteboard = disableCustomiseMode ? null : selectedWhiteboard;
    // const localeData = handoutData.contentData[locale];
    const rawLocaleData = getCurrentContentData(handoutData.contentData, locale, handoutData?.contentId);

    // Ensure all blocks have stable _reactKey from first render
    // IMPORTANT: Use the SAME key generation logic as CustomiseModeContext.ensureBlockKeys
    const localeData = useMemo(() => {
        if (!rawLocaleData) return rawLocaleData;

        return rawLocaleData.map((section) => {
            if (!section.blocks) return section;

            return {
                ...section,
                blocks: section.blocks.map((block, idx) => {
                    // If block already has _reactKey, keep it (from CustomiseMode)
                    if (block._reactKey) return block;

                    // Generate deterministic stable key - MUST MATCH CustomiseModeContext.ensureBlockKeys
                    const valueHash = block.value
                        ? String(block.value)
                              .substring(0, 30)
                              .replace(/[^a-zA-Z0-9]/g, '')
                        : block.type || 'block';
                    const stableKey = `block-${section.sectionName}-${idx}-${valueHash}`;

                    return {
                        ...block,
                        _reactKey: stableKey,
                    };
                }),
            };
        });
    }, [rawLocaleData]);

    // Only show icon if a saved customised handout was loaded from library
    const showCustomisedIcon = !disableCustomiseMode && isLoadedCustomisedHandout;
    const is3DModel = localeData.every((section) => {
        return section.blocks.every((block) => block.type === '3dModel' || block.type === 'documentTitle');
    });

    const [upgradeCTAOpen, setUpgradeCTAOpen] = useState(false);
    const [whiteboardModalOpen, setWhiteboardModalOpen] = useState(false);
    const [, setFavouriteVersion] = useState(0);
    let mainIllustrationRendered = false;
    const { hasUnsavedChanges, setHasUnsavedChanges, promptBeforeAction } = useUnsavedChanges();
    const activeCustomisedId = activeCustomisedHandoutContext?.customisedId || null;
    const isFreePlan = normalizeAccountType(userDataStore?.subscriptionData?.accountType) === 'free';

    const isFavourite = useMemo(() => {
        if (activeCustomisedId) {
            return (userDataStore.userData.customisedHandoutsFavourites || []).includes(activeCustomisedId);
        }

        return isSavedHandoutFavourite(
            handoutData?.contentId,
            userDataStore.userData.savedHandoutsFavourites || [],
            savedHandoutStore?.savedHandoutData || {},
        );
    }, [
        activeCustomisedId,
        handoutData?.contentId,
        savedHandoutStore?.savedHandoutData,
        userDataStore.userData.customisedHandoutsFavourites,
        userDataStore.userData.savedHandoutsFavourites,
    ]);
    const selectedWhiteboardForCurrentHandout = useMemo(() => {
        if (!effectiveSelectedWhiteboard) return null;

        const selectedWhiteboardHandoutId =
            effectiveSelectedWhiteboard.handoutId || effectiveSelectedWhiteboard.baseContentId || null;
        if (selectedWhiteboardHandoutId && selectedWhiteboardHandoutId !== handoutData?.contentId) return null;

        return effectiveSelectedWhiteboard;
    }, [effectiveSelectedWhiteboard, handoutData?.contentId]);

    const syncFavouritePayload = async (payload) => {
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');

        if (isMultiUser) {
            payload.action = 'updateMultiuser';
            payload.emailAddress = userDataStore.userData.emailAddress;
            payload.inviteId = userDataStore.userData.inviteId;
            await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
        } else {
            await UserApi.updateClinicianData(authStore.idToken, payload);
        }
    };

    const handleToggleFavourite = async (event) => {
        event.stopPropagation();
        if (isFreePlan) {
            setUpgradeCTAOpen(true);
            return;
        }

        if (activeCustomisedId) {
            const currentFavourites = userDataStore.userData.customisedHandoutsFavourites || [];
            const isCurrentFavourite = currentFavourites.includes(activeCustomisedId);
            const updatedFavourites = isCurrentFavourite
                ? currentFavourites.filter((id) => id !== activeCustomisedId)
                : [...currentFavourites, activeCustomisedId];

            if (!isCurrentFavourite && currentFavourites.length >= 30) {
                console.warn('You can only mark up to 30 customised handouts as favourites.');
                return;
            }

            userDataStore.setUserData({
                ...userDataStore.userData,
                customisedHandoutsFavourites: updatedFavourites,
            });
            setFavouriteVersion((value) => value + 1);

            try {
                await syncFavouritePayload({ customisedHandoutsFavourites: updatedFavourites });
            } catch (err) {
                console.error('Failed to update customised handout favourites', err);
            }
            return;
        }

        const currentFavourites = userDataStore.userData.savedHandoutsFavourites || [];
        const { updatedFavourites, limitReached } = toggleSavedHandoutFavourite(
            handoutData?.contentId,
            currentFavourites,
            savedHandoutStore?.savedHandoutData || {},
        );

        if (limitReached) {
            console.warn('You can only mark up to 30 saved handouts as favourites.');
            return;
        }

        userDataStore.setUserData({
            ...userDataStore.userData,
            savedHandoutsFavourites: updatedFavourites,
        });
        setFavouriteVersion((value) => value + 1);

        try {
            await syncFavouritePayload({ savedHandoutsFavourites: updatedFavourites });
        } catch (err) {
            console.error('Failed to update saved handout favourites', err);
        }
    };

    useEffect(() => {
        if (!effectiveShowWhiteboard) return;

        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [effectiveShowWhiteboard, hasUnsavedChanges]);

    useEffect(() => {
        if (is3DModel === true && userDataStore?.subscriptionData?.accountType === 'Student') {
            setUpgradeCTAOpen(true);
        }
    }, [is3DModel, userDataStore?.subscriptionData?.accountType]);

    if (!authStore.idToken) {
        throw new Error(`No idToken found`);
    }

    if (!localeData) {
        throw new Error(`No data found for the specified locale: ${locale}`);
    }

    const handleToggleLabels = () => {
        const doToggle = () => setShowLabels((prev) => !prev);
        // If user has been drawing (unsaved canvas changes) and whiteboard is ON,
        // show the same confirmation dialog used for top-bar navigation.
        if (effectiveShowWhiteboard && hasUnsavedChanges) {
            promptBeforeAction(doToggle);
        } else {
            doToggle();
        }
    };

    // Handler for whiteboard selection
    const handleWhiteboardSelect = (whiteboard) => {
        console.log('[Display Handout] Selected whiteboard:', whiteboard);
        setWhiteboardIllustration(whiteboard);
        setWhiteboardModalOpen(false);
    };

    return (
        <>
            <HandoutImageManager handoutData={handoutData}>
                <GlobalStyles
                    styles={{
                        '@media print': {
                            '#handout-content': {
                                paddingBottom: '100px',
                            },
                        },
                    }}
                />
                <HandoutContentProtection
                    id="handout-content"
                    sx={{ my: 2, pb: isMobile ? '130px' : '0px', position: 'relative' }}
                >
                    {localeData.map((section, index) => generateSection(section, index))}
                    {showDisclaimer && <DisclaimerNotice doctorName={doctorName} clinicName={clinicName} />}
                    <Copyright onUploadLogoClick={onUploadLogoClick} />
                </HandoutContentProtection>
            </HandoutImageManager>

            <CTAUpgradeDialog
                open={upgradeCTAOpen}
                handleClose={() => {
                    setUpgradeCTAOpen(false);
                }}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Unlock saving, customising and more with just one click!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to enjoy full access!"
                buttonText="Upgrade plan"
                benefits={[
                    'Save whiteboards and customised handouts',
                    'Replace illustrations with your own whiteboards',
                    'Unlimited digital sharing of all handouts & anatomy diagrams',
                ]}
                imageUrl={ctaUpgradeProfessionalPaywall}
                isLocked={false}
            />

            {/* Whiteboard Selection Modal */}
            <WhiteboardSelectionModal
                open={whiteboardModalOpen}
                onClose={() => setWhiteboardModalOpen(false)}
                onSelect={handleWhiteboardSelect}
                baseContentId={handoutData.contentId}
                hasActiveDrawings={hasActiveWhiteboardDrawings}
            />
        </>
    );

    function generateSection(jsonContent, index) {
        const sectionName = jsonContent.sectionName;
        const isPrintPageBreak = sectionName === 'printPageBreak';
        let blocks = jsonContent.blocks;

        // Filter blocks based on toggle states
        blocks = blocks.filter((block) => {
            if (userDataStore?.subscriptionData?.accountType === 'Student' && block.type === '3dModel') {
                return false;
            }
            return true;
        });

        if (blocks.length === 0) {
            return null; // Skip rendering this section if no blocks remain
        }

        return (
            <React.Fragment key={index}>
                <Box
                    component="section"
                    className={`${sectionName}${isPrintPageBreak ? ' printPageBreak' : ''}`}
                    sx={{
                        my: 4,
                        position: 'relative',
                        '@media print': {
                            pageBreakBefore: isPrintPageBreak ? 'auto' : 'avoid',
                            zIndex: 1,
                        },
                    }}
                >
                    {blocks.map((block, index) => (
                        <React.Fragment key={block._reactKey}>
                            {blockToComponentMapping(block, sectionName, index)}
                        </React.Fragment>
                    ))}
                </Box>
                {isPrintPageBreak && <div></div>}
            </React.Fragment>
        );
    }

    // top level component which checks CMS block type then generates MUI/custom component for different blocks
    function blockToComponentMapping(block, sectionName, index) {
        if (!block || !block.type) {
            console.error(`Block is missing type or is undefined`, block);
            return null;
        }
        switch (block.type) {
            case 'documentTitle': {
                const isDiagramHandout = localeData?.some((s) => s.sectionName === 'diagram');
                const shouldOffsetDiagramTitle = isDiagramHandout;
                return (
                    <Box
                        key={block.value}
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 1,
                            pt: shouldOffsetDiagramTitle ? '60px' : 0,
                        }}
                    >
                        <Typography
                            component="h1"
                            variant="documentTitle"
                            sx={{
                                flex: 1,
                                pr: showCustomisedIcon ? '180px' : '64px',
                                ...(isDiagramHandout && { fontSize: { xs: '20pt', sm: '35pt' } }),
                            }}
                        >
                            {block.value}
                        </Typography>
                        {isDiagramHandout && (
                            <Box sx={{ flexShrink: 0, pb: '4px' }}>
                                <FormControlLabel
                                    control={<AntSwitch checked={showLabels} onChange={handleToggleLabels} />}
                                    label="Labels"
                                    labelPlacement="start"
                                    sx={{ '& .MuiFormControlLabel-label': { marginRight: '8px' } }}
                                />
                            </Box>
                        )}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: { xs: 4, sm: -4 },
                                right: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                            }}
                        >
                            {showCustomisedIcon && (
                                <Box
                                    sx={{
                                        width: '115px',
                                        height: '50px',
                                        backgroundColor: '#CCCCCC',
                                        borderRadius: '25px',
                                        pointerEvents: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter',
                                            fontWeight: 400,
                                            fontSize: { xs: '0.85rem', sm: '1rem' },
                                            letterSpacing: '-0.5px',
                                            color: '#FFFFFF',
                                            textTransform: 'none',
                                        }}
                                    >
                                        Edited
                                    </Typography>
                                </Box>
                            )}
                            <FavouriteButton
                                isFavourite={isFavourite}
                                onClick={handleToggleFavourite}
                                size={50}
                                iconSize={50}
                                tooltipTitle={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
                            />
                        </Box>
                    </Box>
                );
            }
            case 'sectionTitle':
                return (
                    <Typography
                        component="h2"
                        key={block.value}
                        variant="sectionTitle"
                        sx={{ my: 2, color: '#000453' }}
                    >
                        {block.value}
                    </Typography>
                );
            case 'sectionSubtitle':
                return (
                    <Typography component="h3" key={block.value} variant="sectionSubtitle" sx={{ my: 2 }}>
                        {block.value}
                    </Typography>
                );
            case 'calloutSmall':
                return <Callout key={block.value}>{block.value}</Callout>;
            case 'calloutMedium':
                return <Callout key={block.value}>{block.value}</Callout>;
            case 'paragraph':
                // Wrap paragraph in EditableBlock if customise mode is active
                const paragraphComponent = (
                    <Typography
                        key={block.value}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            pb: 1,
                            '@media print': {
                                pageBreakInside: 'auto',
                                pageBreakBefore: 'auto',
                                display: 'inline',
                            },
                        }}
                    >
                        {block.value}
                    </Typography>
                );

                if (disableCustomiseMode) {
                    return paragraphComponent;
                }

                // Wrap in EditableBlock to enable editing
                return (
                    <EditableBlock
                        key={block._reactKey || `${sectionName}-${index}`}
                        block={block}
                        sectionIndex={localeData.findIndex((s) => s.sectionName === sectionName)}
                        blockIndex={index}
                    >
                        {paragraphComponent}
                    </EditableBlock>
                );
            case 'list':
                const listComponent = (
                    <BulletList key={block._reactKey || `${sectionName}-${index}`} items={block.values} />
                );
                if (disableCustomiseMode) {
                    return listComponent;
                }

                return (
                    <EditableList
                        key={block._reactKey || `${sectionName}-${index}`}
                        block={block}
                        sectionIndex={localeData.findIndex((s) => s.sectionName === sectionName)}
                        blockIndex={index}
                    >
                        {listComponent}
                    </EditableList>
                );
            case 'numberList':
                return <NumberList items={block.values} />;
            case 'iconStrip':
                return (
                    <IconStrip
                        iconDetails={block.iconDetails}
                        imageStore={imageStore}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            '@media print': {
                                pageBreakInside: 'avoid',
                                pageBreakBefore: 'auto',
                                pb: 1,
                                display: 'flex',
                            },
                        }}
                    />
                );

            case 'illustration': {
                const isDiagramSection = sectionName === 'diagram';

                // If Diagram
                if (isDiagramSection) {
                    // Decide if *this* block is displayed based on showLabels
                    const isDisplayedBlock = index === (showLabels ? 0 : 1);
                    if (!isDisplayedBlock) {
                        // Skip rendering if it's the "other" block
                        return null;
                    }

                    // If it is the displayed block (this chnaged one), place the whiteboard on it
                    // Use stable key based on handout ID to prevent unmounting
                    const diagramKey = `illustration-${handoutData.contentId}-diagram`;
                    // When a saved diagram WB is loaded as a CH, show the merged PNG in view mode.
                    // Only swap if the saved diagramVariant matches the current labels state.
                    const savedVariantMatchesLabels =
                        !selectedWhiteboardForCurrentHandout?.diagramVariant ||
                        (showLabels && selectedWhiteboardForCurrentHandout.diagramVariant === 'withLabels') ||
                        (!showLabels && selectedWhiteboardForCurrentHandout.diagramVariant === 'noLabels');
                    const diagramSrc =
                        !effectiveShowWhiteboard &&
                        selectedWhiteboardForCurrentHandout?.canvasImageLink &&
                        savedVariantMatchesLabels
                            ? selectedWhiteboardForCurrentHandout.canvasImageLink
                            : block.src;
                    return (
                        <Box key={diagramKey} sx={{ position: 'relative', display: 'inline-block' }}>
                            <HandoutImageLoader
                                src={diagramSrc}
                                alt={block.alt}
                                imageStore={imageStore}
                                id="main-illustration-img"
                            />
                            {effectiveShowWhiteboard && (
                                <Box
                                    key={`whiteboard-wrapper-${handoutData.contentId}-diagram`}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                    }}
                                >
                                    <Whiteboard
                                        key={`whiteboard-${handoutData.contentId}-diagram`}
                                        backgroundImage={block.src}
                                        showWhiteboard={effectiveShowWhiteboard}
                                        activeTool={activeTool}
                                        color={color}
                                        thickness={thickness}
                                        canvasRef={canvasRef}
                                        onUnsavedChanges={onUnsavedChanges}
                                        setToolbarExpanded={setToolbarExpanded}
                                        suppressUnsavedChangesRef={suppressUnsavedChangesRef}
                                        onColorUsed={onColorUsed}
                                        onCanvasModified={onCanvasModified}
                                    />
                                </Box>
                            )}
                        </Box>
                    );
                }

                // If NOT a Diagram section, show "Only the first illustration",
                if (!mainIllustrationRendered) {
                    mainIllustrationRendered = true; // toggle so the next illustration won't get Whiteboard
                    const mainKey = `illustration-${handoutData.contentId}-main`;
                    return (
                        <Box key={mainKey}>
                            {showMainIllustration && (
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    <HandoutImageLoader
                                        src={selectedWhiteboardForCurrentHandout?.canvasImageLink || block.src}
                                        alt={block.alt}
                                        imageStore={imageStore}
                                        id="main-illustration-img"
                                    />
                                    {effectiveShowWhiteboard && (
                                        <Box
                                            key={`whiteboard-wrapper-${handoutData.contentId}-main`}
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                            }}
                                        >
                                            <Whiteboard
                                                key={`whiteboard-${handoutData.contentId}-main`}
                                                backgroundImage={
                                                    selectedWhiteboardForCurrentHandout?.canvasImageLink || block.src
                                                }
                                                showWhiteboard={effectiveShowWhiteboard}
                                                activeTool={activeTool}
                                                color={color}
                                                thickness={thickness}
                                                canvasRef={canvasRef}
                                                onUnsavedChanges={setHasUnsavedChanges}
                                                setToolbarExpanded={setToolbarExpanded}
                                                suppressUnsavedChangesRef={suppressUnsavedChangesRef}
                                                onColorUsed={onColorUsed}
                                                onCanvasModified={onCanvasModified}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            )}
                            {/* Bottom row: Show illustration toggle (left) + Replace Illustration button (right) */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                {onToggleDiagram && !effectiveIsCustomiseMode && (
                                    <FormControlLabel
                                        control={
                                            <HandoutVisibilitySwitch
                                                checked={showMainIllustration}
                                                onChange={onToggleDiagram}
                                            />
                                        }
                                        label="Show illustration"
                                        sx={handoutVisibilityToggleSx}
                                    />
                                )}
                                {(effectiveIsCustomiseMode || effectiveShowWhiteboard) &&
                                    !hasActiveWhiteboardDrawings && (
                                        <Button
                                            variant="contained"
                                            startIcon={<ImageIcon />}
                                            onClick={() => {
                                                if (
                                                    userDataStore?.subscriptionData?.accountType === 'Free' ||
                                                    userDataStore?.subscriptionData?.accountType === 'Student'
                                                ) {
                                                    setUpgradeCTAOpen(true);
                                                    return;
                                                }
                                                setWhiteboardModalOpen(true);
                                            }}
                                            sx={{
                                                bgcolor: '#FF669B',
                                                '&:hover': { bgcolor: '#FF4D88' },
                                                textTransform: 'none',
                                                ml: 'auto',
                                            }}
                                        >
                                            Replace Illustration
                                        </Button>
                                    )}
                            </Box>
                        </Box>
                    );
                } else {
                    // normal handout: just an image
                    return <HandoutImageLoader src={block.src} alt={block.alt} imageStore={imageStore} />;
                }
            }
            case '3dModel':
                return (
                    <Box>
                        {show3DModel && (
                            <Model3DViewer
                                uid={block.uid}
                                alt={block.alt}
                                author={block.author}
                                authorWeb={block.authorWeb}
                                hideAttribution={!!onToggle3DModel}
                            />
                        )}
                        {/* Toggle (left) + attribution (right) on same row */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mt: 1,
                                mb: show3DModel && block.author ? 3 : 0,
                            }}
                        >
                            {onToggle3DModel && !effectiveIsCustomiseMode && (
                                <FormControlLabel
                                    control={
                                        <HandoutVisibilitySwitch checked={show3DModel} onChange={onToggle3DModel} />
                                    }
                                    label="Show 3D model"
                                    sx={handoutVisibilityToggleSx}
                                />
                            )}
                            {show3DModel && block.author && (
                                <Typography
                                    variant="body2"
                                    sx={{ fontSize: '14px', color: '#555', textAlign: 'right' }}
                                >
                                    Model created by{' '}
                                    {block.authorWeb ? (
                                        <a
                                            href={block.authorWeb}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#0073e6', textDecoration: 'underline' }}
                                        >
                                            {block.author}
                                        </a>
                                    ) : (
                                        block.author
                                    )}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            case 'tutorialVideo':
                return (
                    <Box sx={{ my: 2 }}>
                        {showTutorialVideo && (
                            <video width="100%" controls>
                                <source src={block.src} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        )}
                        {onToggleTutorialVideo && !effectiveIsCustomiseMode && (
                            <FormControlLabel
                                control={
                                    <HandoutVisibilitySwitch
                                        checked={showTutorialVideo}
                                        onChange={onToggleTutorialVideo}
                                    />
                                }
                                label="Show tutorial video"
                                sx={{ ...handoutVisibilityToggleSx, mt: 1 }}
                            />
                        )}
                    </Box>
                );
            case 'printPageBreak':
                return (
                    <>
                        <div className="printPageBreak"></div>
                        <p></p>
                    </>
                );

            default:
                return null;
        }
    }
}

export default DisplayHandout;
